'use client'
import { type FC, useState } from 'react'
import { Box, Flex, Heading, HStack, Stack, Text, VStack } from '@chakra-ui/react'
import { GrandLink, GrandSection } from 'ui'
import fetchJson, { FetchError } from 'api/utils/next/fetchJson'
import { useRouter } from 'next/navigation'
import { LoginForm, type LoginFormType } from 'forms'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { OverridesContextType } from 'ui/lib/overrides'
import { OverridesContextProvider, overrideText } from 'ui/lib/overrides'
import type { User } from '../../utils'
import { useUser } from '../../index'

interface LoginPageStdConfig {
  paths: {
    loginApiRoute: string
  }
  redirects: {
    onLoggedIn: string
    toNewPassword: string
    toSignup: string
  }
  overrides?: OverridesContextType
}

export interface LoginPageStdOverrides {
  loginPageTitle?: string
  loginPageSubtitle?: string
  loginForgottenPwd?: string
  loginForgottenPwdDesc?: string
  registerAccount?: string
  loginPageNewHere?: string
}

interface LoginPageStdProps {
  config: LoginPageStdConfig
}

const LoginPageStd: FC<LoginPageStdProps> = ({ config }) => {
  const { mutateUser } = useUser()
  const router = useRouter()
  const overrides = useOverridesContext<keyof LoginPageStdOverrides>()
  const [errorMsg, setErrorMsg] = useState('')

  const title = overrideText('Logga in', overrides?.loginPageTitle)
  const subtitle = overrideText(
    'Logga in på ditt konto för att se beställningar och hantera din profil.',
    overrides?.loginPageSubtitle
  )
  const forgottenLabel = overrideText('Glömt lösenord?', overrides?.loginForgottenPwd)
  const newHereLabel = overrideText('Ny här?', overrides?.loginPageNewHere)
  const registerLabel = overrideText('Skapa konto', overrides?.registerAccount)

  const handleLogin = async (data: LoginFormType) => {
    try {
      const user: User = await fetchJson(config.paths.loginApiRoute, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password })
      })
      await mutateUser(user, false)
      setErrorMsg('')
      // Invalidate cached RSC payloads so the new session cookie is picked up
      // by server-side gates on the next navigation.
      router.refresh()
      router.push(config.redirects.onLoggedIn)
    } catch (error) {
      if (error instanceof FetchError) {
        setErrorMsg(error.data.message)
      } else {
        console.error('An unexpected error happened:', error)
      }
    }
  }

  return (
    <OverridesContextProvider overrides={{ ...config.overrides }}>
      <GrandSection sectionId="login">
        <Flex justify="center" w="full">
          <VStack
            align="stretch"
            bg="background.default"
            borderColor="gray.100"
            borderRadius="md"
            borderWidth={1}
            maxW="md"
            p={{ base: 6, md: 8 }}
            spacing={6}
            w="full"
          >
            <VStack align="stretch" spacing={2}>
              <Heading as="h1" fontSize={{ base: '2xl', md: '3xl' }}>
                {title}
              </Heading>
              <Text color="text.mild" fontSize="sm">
                {subtitle}
              </Text>
            </VStack>

            <LoginForm error={errorMsg} onSubmit={handleLogin} />

            <Stack
              align={{ base: 'stretch', sm: 'center' }}
              direction={{ base: 'column', sm: 'row' }}
              fontSize="sm"
              justify="space-between"
              spacing={2}
            >
              <GrandLink color="primary" href={config.redirects.toNewPassword}>
                {forgottenLabel}
              </GrandLink>
              <HStack spacing={1}>
                <Text color="text.mild">{newHereLabel}</Text>
                <GrandLink color="primary" fontWeight="semibold" href={config.redirects.toSignup}>
                  {registerLabel}
                </GrandLink>
              </HStack>
            </Stack>

            <Box pt={2}>
              <Text color="text.mild" fontSize="xs">
                {overrideText(
                  'Ett konto skapas automatiskt åt dig när du lägger din första beställning och anvisningar skickas med din orderbekräftelse.',
                  overrides?.loginForgottenPwdDesc
                )}
              </Text>
            </Box>
          </VStack>
        </Flex>
      </GrandSection>
    </OverridesContextProvider>
  )
}

export default LoginPageStd
